import { useState, useMemo } from 'react';

export type Order = 'asc' | 'desc';

export interface SortConfig<T> {
  orderBy: keyof T | '';
  order: Order;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const valA = a[orderBy];
  const valB = b[orderBy];

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function useSortableTable<T>(
  data: T[],
  defaultOrderBy: keyof T | '' = '',
  defaultOrder: Order = 'asc'
) {
  const [orderBy, setOrderBy] = useState<keyof T | ''>(defaultOrderBy);
  const [order, setOrder] = useState<Order>(defaultOrder);

  const handleRequestSort = (property: keyof T) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return data;
    return stableSort(data, getComparator(order, orderBy));
  }, [data, order, orderBy]);

  return {
    orderBy,
    order,
    handleRequestSort,
    sortedData,
  };
}
